/**
 * FauxDS Checkbox - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Checkbox component for prototyping.
 * It is NOT the real @faire/slate Checkbox and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Checkbox, see: @faire/slate/components/checkbox
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 3593-5 (Slate Checkbox)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Unchecked, Checked, Indeterminate (with/without label, enabled/disabled)
 * - Font: Graphik, 14px, 400 weight, 20px line-height, 0.15px letter-spacing
 * - Checkbox size: 16px × 16px
 * - Border radius: 2px
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useId } from "react";

export type CheckboxChecked = boolean | "indeterminate";

export interface CheckboxProps {
  /**
   * Whether the checkbox is checked
   * - true: checked
   * - false: unchecked
   * - "indeterminate": partially checked
   * @default false
   */
  checked?: CheckboxChecked;
  /**
   * Default checked state (uncontrolled)
   */
  defaultChecked?: boolean;
  /**
   * Change handler
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Whether the checkbox is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * The checkbox label
   */
  label?: string;
  /**
   * Checkbox name attribute
   */
  name?: string;
  /**
   * Checkbox value attribute
   */
  value?: string;
  /**
   * Aria-label for accessibility (required if no label)
   */
  "aria-label"?: string;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * Checkbox - FauxDS (recreated from Figma MCP)
 *
 * Checkboxes enable users to select one or more items from a list.
 * This implementation was created by extracting design specs directly from Figma.
 */
const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      defaultChecked,
      onChange,
      disabled = false,
      label,
      name,
      value,
      "aria-label": ariaLabel,
      className,
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = `checkbox-figma-${generatedId}`;

    const isIndeterminate = checked === "indeterminate";
    const isChecked = checked === true;

    // Determine visual state for styling based on Figma design tokens
    const getStateStyles = () => {
      if (disabled) {
        return {
          border: "border-[var(--slate-color-action-border-disabled)]", // #dfe0e1
          bg:
            isChecked || isIndeterminate
              ? "bg-[var(--slate-color-action-surface-disabled)]" // #f7f7f7
              : "bg-transparent",
          icon: "text-[var(--slate-color-action-icon-disabled)]", // #757575
          label: "text-[var(--slate-color-action-text-disabled)]", // #757575
        };
      }
      if (isChecked || isIndeterminate) {
        return {
          border: "border-[var(--slate-color-action-border-active)]", // #000000
          bg: "bg-[var(--slate-color-action-surface-active)]", // #000000
          icon: "text-[var(--slate-color-action-icon-inverse)]", // #ffffff
          label: "text-[var(--slate-color-action-text-default)]", // #333333
        };
      }
      return {
        border: "border-[var(--slate-color-action-border-default)]", // #333333
        bg: "bg-transparent",
        icon: "text-[var(--slate-color-action-icon-default)]", // #333333
        label: "text-[var(--slate-color-action-text-default)]", // #333333
      };
    };

    const stateStyles = getStateStyles();

    // Checkmark SVG (8px × 8px, stroke 1.5)
    const CheckIcon = () => (
      <svg
        width="8"
        height="8"
        viewBox="0 0 8 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={stateStyles.icon}
      >
        <path
          d="M1 4L3 6L7 2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    // Indeterminate (minus) SVG (8px × 8px, stroke 1.5)
    const IndeterminateIcon = () => (
      <svg
        width="8"
        height="8"
        viewBox="0 0 8 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={stateStyles.icon}
      >
        <path
          d="M1 4H7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );

    return (
      <div className="flex">
        {/* Checkbox box - 16px × 16px, using Figma design tokens */}
        <div
          className={cn(
            "relative flex h-[16px] w-[16px] min-w-[16px] items-center justify-center border",
            stateStyles.border,
            stateStyles.bg,
            className
          )}
          style={{
            borderRadius: "var(--slate-radius-component-checkbox)",
          }}
        >
          {/* Hidden input */}
          <input
            ref={ref}
            type="checkbox"
            id={inputId}
            name={name}
            value={value}
            checked={isChecked}
            defaultChecked={defaultChecked}
            onChange={onChange}
            disabled={disabled}
            aria-checked={isIndeterminate ? "mixed" : isChecked}
            aria-label={!label ? ariaLabel : undefined}
            className={cn(
              "absolute h-full w-full cursor-pointer appearance-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--slate-color-action-border-focus-ring)] disabled:cursor-not-allowed"
            )}
          />
          {/* Icon */}
          {isChecked && <CheckIcon />}
          {isIndeterminate && <IndeterminateIcon />}
        </div>

        {/* Label - Using Figma typography tokens */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "cursor-pointer pl-[8px] select-none",
              stateStyles.label,
              disabled ? "cursor-not-allowed" : ""
            )}
            style={{
              fontFamily: "var(--slate-typography-family-sans)",
              fontSize: "var(--slate-typography-font-size-200)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-200)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
